/**
 * Build batches of 360° frame indices to load.
 * First batch centers on frame 0 and loads N on each side, then expands in waves.
 */

const wrapIndex = (i: number, total: number): number => (i + total) % total;

/**
 * Create the initial batch centered on a frame with a number of items per side
 */
export const makeInitialBatch = (
  total: number,
  perSide: number,
  center = 0,
): { batch: number[]; seen: Set<number> } => {
  const seen = new Set<number>();
  const batch: number[] = [];
  const pushIfNew = (idx: number) => {
    if (!seen.has(idx)) {
      seen.add(idx);
      batch.push(idx);
    }
  };
  pushIfNew(wrapIndex(center, total));
  for (let d = 1; d <= perSide; d++) {
    const left = wrapIndex(center - d, total);
    const right = wrapIndex(center + d, total);
    pushIfNew(left);
    if (right !== left) pushIfNew(right);
  }

  return { batch, seen };
};

/**
 * Build wave batches, expanding outwards from the initial radius by `waveSize` per wave
 */
export const makeWaveBatches = (
  total: number,
  startDistance: number,
  waveSize: number,
  seen: Set<number>,
  center = 0,
): number[][] => {
  const batches: number[][] = [];
  const pushIfNew = (arr: number[], idx: number) => {
    if (!seen.has(idx)) {
      seen.add(idx);
      arr.push(idx);
    }
  };

  for (let d = startDistance; d <= Math.floor(total / 2); d += waveSize) {
    const batch: number[] = [];
    for (let i = 0; i < waveSize; i++) {
      const dist = d + i;
      const left = wrapIndex(center - dist, total);
      const right = wrapIndex(center + dist, total);
      pushIfNew(batch, left);
      pushIfNew(batch, right);
    }

    if (batch.length) batches.push(batch);
  }

  return batches;
};

/**
 * Build overall batches, starting with the initial centered batch, followed by wave batches,
 * and finally any remaining indices not picked up by the wave iteration
 */
export const buildLoadingBatches = (
  total: number,
  initialPerSide: number,
  waveSize: number,
): number[][] => {
  const { batch: initialBatch, seen } = makeInitialBatch(total, initialPerSide);
  const waves = makeWaveBatches(total, initialPerSide + 1, waveSize, seen);

  const batches: number[][] = [initialBatch, ...waves];

  // Include any remaining indices not yet covered
  if (seen.size < total) {
    const remaining: number[] = [];
    for (let i = 0; i < total; i++) if (!seen.has(i)) remaining.push(i);
    if (remaining.length) batches.push(remaining);
  }

  return batches;
};
